import React, { useEffect, useState, useMemo, useCallback } from "react";
import FormField from "../../Common/FormFeild";
import DynamicArray from "../../ContentCreator/DynamicArray";
import Button from "../../Common/Button";
import CompareModal from "../../ContentCreator/CompareModal";
import PreviewModal from "./PreviewModal";
import { getDoctorByIdAPI, saveDraftAndReviewAPI, SaveDraftPayload } from "../../Api/MarketingApi";
import LanguageMultiSelect from "../../Common/LanguageSelect";
import { Department, getDepartmentAPI, getDesignationAPI, getLanguageAPI, getLocationAPI, getSpecializationAPI, getTitleAPI, Specialization } from "../../Api/LookUp";
import SearchableDropdown from "../../Common/SearchableDropdown";
import { MarketingFormData } from "../../Types/marketing";
import { Toast } from "../../Common/Toast";
import axios from "axios";
import LocationMultiSelect from "../../Common/MultiLocationSelector";
import Doctor from "./../../../images/doctorAvtar.png"
/* ================= TYPES ================= */

interface RoleDescription {
  role: string;
  description: string;
}

interface DoctorData {
  isSuccess: boolean;
  departmentName: string;
  doctorId: number;
  namePrefixCode: string;
  locationId: number;
  locationName: string;
  profileBrief: string;
  name: string;
  qualification: string;
  designation: string;
  areaOfInterest: string;
  awardsAchievements: string | null;
  researchPublications: string | null;
  previousExperiance: string;
  languages: string;
  phone: string;
  empCode:string;
  source:string;
  experienceYears: number;
  doctorFees: number;
  status: string;
  specializationId?: number;
  gender: string;
  phoneNo: string;
  emailId: string;
  registrationNo: string;
  doctorImage?: string; // ✅ Add doctorImage from tempData
    secondaryLocations?: string | null;
}

interface LookupOption {
  id: number;
  label: string;
}

interface MarketingEditorProps {
  doctorId: number;
  onClose: () => void;
}

interface Language {
  LangId: number;
  Name: string;
}

interface ValidationErrors {
  designation?: string;
  experience?: string;
  departmentName?:string;
  specializationName?:string;
  locationName?: string;
  fee?: string;
  languages?: string;
  namePrefixCode?: string;
  summary?: string;
  specialization?: string;
  doctorImage?: string;
  gender?: string;
  phoneNo?: string;
  emailId?: string;
  empCode?:string;
 
}

interface DynamicFieldErrors {
  [key: string]: string;
}

/* ================= UTILITY FUNCTIONS ================= */

const safeJSONParse = <T,>(jsonString: string | null, defaultValue: T): T => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString) as T;
  } catch (e) {
    console.error("JSON parse error:", e, "Input:", jsonString);
    return defaultValue;
  }
};

const parseRoleDescriptionArray = (jsonString: string | null): RoleDescription[] => {
  if (!jsonString) return [];
  try {
    const parsed = JSON.parse(jsonString);
    return parsed.map((item: any) => ({
      role: item.Role || item.role || "",
      description: item.Description || item.description || ""
    }));
  } catch (e) {
    console.error("JSON parse error:", e);
    return [];
  }
};

const hasEmptyFields = <T extends Record<string, any>>(
  items: T[],
  fields: (keyof T)[]
): boolean => {
  if (items.length === 0) return false;
  return items.some(item => fields.some(field => !String(item[field]).trim()));
};

const extractNumericValue = (value: string): number => {
  return parseInt(value.replace(/\D/g, "")) || 0;
};

/* ================= REUSABLE COMPONENTS ================= */

const ValidationErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
    {message}
  </p>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="mb-2 w-fit max-w-full">
    <div className="flex items-center gap-2 px-3 py-1 text-xs text-red-700 bg-red-50 border-l-4 border-red-500 rounded-md shadow-sm">
      <svg className="w-4 h-4 flex-shrink-0 text-red-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      <span>{message}</span>
    </div>
  </div>
);

/* ================= MAIN COMPONENT ================= */

const MarketingEditor: React.FC<MarketingEditorProps> = ({ doctorId, onClose }) => {
  const userId = sessionStorage.getItem("userId");
  
  // State
  const [doctorData, setDoctorData] = useState<DoctorData | null>(null);
  const [formData, setFormData] = useState<MarketingFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [compareSection, setCompareSection] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<DoctorData | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [dynamicFieldErrors, setDynamicFieldErrors] = useState<DynamicFieldErrors>({});
  const [locations, setLocations] = useState<LookupOption[]>([]);
  const [departments, setAvailableDepartment] = useState<Department[]>([]);
  const [specializations, setAvailableSpecialization] = useState<Specialization[]>([]);
  const [designations, setDesignations] = useState<LookupOption[]>([]);
  const [titles, setTitles] = useState<LookupOption[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);

  // ✅ Image state variables
  const [doctorImageFile, setDoctorImageFile] = useState<File | null>(null);
  const [doctorImagePreview, setDoctorImagePreview] = useState<string>("");
  const [uploadedImagePath, setUploadedImagePath] = useState<string>("");
  const [tempDataDoctorImage, setTempDataDoctorImage] = useState<string>(""); // ✅ Store tempData image

  const MAXFILESIZEBYTES = 5 * 1024 * 1024; // 5MB

  // ✅ Upload image to API
  const uploadDoctorImageToAPI = async (file: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("DoctorImage", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Marketing/uploadimage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success && response.data.filePath) {
        return response.data.filePath;
      } else {
        throw new Error(response.data.Message || "Image upload failed");
      }
    } catch (error: any) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };
// Parse secondaryLocations from JSON string like "[{\"LocationId\":1},{\"LocationId\":2}]"
const parseSecondaryLocations = (jsonString: string | null | undefined): number[] => {
  if (!jsonString) return [];
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      return parsed.map((item: any) => item.LocationId || item.locationId).filter(Boolean);
    }
    return [];
  } catch (e) {
    console.error("Secondary locations parse error:", e);
    return [];
  }
};

const handleEmployeeCodeChange = (value: string) => {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9\-\/]/g, "")
    .slice(0, 30);
};
  // ✅ Handle photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAXFILESIZEBYTES) {
      Toast.error("Photo size must be less than or equal to 5MB");
      e.target.value = "";
      return;
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'webp') {
      Toast.error("Only WEBP format is accepted");
      e.target.value = "";
      return;
    }

    try {
      setLoading(true);
      setAlert("📤 Uploading image...");

      const imagePath = await uploadDoctorImageToAPI(file);
      
      setDoctorImageFile(file);
      setUploadedImagePath(imagePath);
      setTempDataDoctorImage(""); // ✅ Clear tempData image when new image is uploaded
      const previewUrl = URL.createObjectURL(file);
      setDoctorImagePreview(previewUrl);
      
      Toast.success("Photo uploaded successfully");
      setAlert("");

      if (validationErrors.doctorImage) {
        setValidationErrors((prev) => {
          const { doctorImage, ...rest } = prev;
          return rest;
        });
      }
    } catch (err: any) {
      console.error("Photo upload error:", err);
      setDoctorImageFile(null);
      setDoctorImagePreview("");
      setUploadedImagePath("");
      Toast.error(err?.response?.data?.Message || "Photo upload failed");
      setAlert("");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  // ✅ Handle delete photo
  const handleDeletePhoto = () => {
    if (doctorImagePreview) {
      URL.revokeObjectURL(doctorImagePreview);
    }
    setDoctorImageFile(null);
    setDoctorImagePreview("");
    setUploadedImagePath("");
    setTempDataDoctorImage(""); // ✅ Also clear tempData image
  };

  const validationState = useMemo(() => {
    if (!formData) return { areaOfInterest: false, previousExperiance: false, awards: false, publications: false };
    
    return {
      areaOfInterest: hasEmptyFields(formData.areaOfInterest, ["role", "description"]),
      previousExperiance: hasEmptyFields(formData.previousExperiance, ["role", "description"]),
      awards: formData.awards.length > 0 && formData.awards.some(item => !item.trim()),
      publications: formData.publications.length > 0 && formData.publications.some(item => !item.trim())
    };
  }, [formData]);

  const normalize = (v: any) =>
    typeof v === "string"
      ? v.trim()
      : JSON.stringify(v ?? "");

  // Comprehensive validation
  const validateAllDynamicFields = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const newDynamicErrors: DynamicFieldErrors = {};
    
    if (validationState.areaOfInterest) {
      errors.push("Area of Interest has empty fields");
      newDynamicErrors.areaOfInterest = "Fill in all existing fields before proceeding";
    }
    
    if (validationState.previousExperiance) {
      errors.push("Previous Experience has empty fields");
      newDynamicErrors.previousExperiance = "Fill in all existing fields before proceeding";
    }
    
    if (validationState.awards) {
      errors.push("Awards & Achievements has empty fields");
    }
    
    if (validationState.publications) {
      errors.push("Research & Publications has empty fields");
    }
    
    setDynamicFieldErrors(newDynamicErrors);
    return {
      isValid: errors.length === 0,
      errors
    };
  }, [validationState]);

  // Clear dynamic errors when fields are fixed
  useEffect(() => {
    if (!validationState.areaOfInterest && dynamicFieldErrors.areaOfInterest) {
      setDynamicFieldErrors(prev => {
        const { areaOfInterest, ...rest } = prev;
        return rest;
      });
    }
  }, [validationState.areaOfInterest, dynamicFieldErrors.areaOfInterest]);

  useEffect(() => {
    if (!validationState.previousExperiance && dynamicFieldErrors.previousExperiance) {
      setDynamicFieldErrors(prev => {
        const { previousExperiance, ...rest } = prev;
        return rest;
      });
    }
  }, [validationState.previousExperiance, dynamicFieldErrors.previousExperiance]);

  // Fetch Languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await getLanguageAPI();
        setAvailableLanguages(response.data);
      } catch (error) {
        console.error("error in language listing", error);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        const response = await getDepartmentAPI();
        setAvailableDepartment(response.data);
      } catch (error) {
        console.error("error in Department listing", error);
      }
    };
    fetchDepartment();
  }, []);

  // Fetch Specializations when department changes
  useEffect(() => {
    const fetchSpecializations = async () => {
      if (!selectedDepartmentId) {
        setAvailableSpecialization([]);
        return;
      }

      try {
        const response = await getSpecializationAPI(selectedDepartmentId);
        setAvailableSpecialization(response.data);
      } catch (error) {
        console.error("error in Specialization listing", error);
        setAvailableSpecialization([]);
      }
    };
    fetchSpecializations();
  }, [selectedDepartmentId]);
const toggleSecondaryLocation = (locId: number) => {
  setFormData(prev => {
    if (!prev) return prev;
    const exists = prev.secondaryLocations.includes(locId);
    return {
      ...prev,
      secondaryLocations: exists
        ? prev.secondaryLocations.filter(id => id !== locId)
        : [...prev.secondaryLocations, locId],
    };
  });
};

// ensure primary is not added as secondary
const availableSecondaryLocationOptions = useMemo(
  () => locations.filter(l => l.id !== (formData?.locationId ?? 0)),
  [locations, formData?.locationId]
);
  const transformDoctorDataToFormData = useCallback((apiData: DoctorData): MarketingFormData => {
    return {
      name: apiData.name,
      namePrefixCode: apiData.namePrefixCode,
      locationId: apiData.locationId,
      locationName: apiData.locationName,
      designationId: null,
      designation: apiData.designation,
      experience: `${apiData.experienceYears}`,
      fee: `${apiData.doctorFees}`,
      departmentName: `${apiData.departmentName}`,
  empCode: apiData.empCode ?? "",
source: apiData.source ?? "",
      languages: apiData.languages,
      qualification: apiData.qualification,
      summary: apiData.profileBrief,
      benefits: "",
      awards: safeJSONParse<string[]>(apiData.awardsAchievements, []),
      publications: safeJSONParse<string[]>(apiData.researchPublications, []),
      areaOfInterest: parseRoleDescriptionArray(apiData.areaOfInterest),
      previousExperiance: parseRoleDescriptionArray(apiData.previousExperiance),
      specializationId: apiData.specializationId || null,
      gender: apiData.gender,
      phoneNo: apiData.phoneNo,
      emailId: apiData.emailId,
      registrationNo: apiData.registrationNo,
         secondaryLocations: parseSecondaryLocations(apiData.secondaryLocations), 
    };
  }, []);

  // Fetch Locations, Designations, Titles
  useEffect(() => {
    getLocationAPI().then(res =>
      setLocations(res.data.map((l: any) => ({
        id: l.LocationId,
        label: l.LocationName
      })))
    );

    getDesignationAPI().then(res =>
      setDesignations(res.data.map((d: any) => ({
        id: d.Id,
        label: d.Designation
      })))
    );

    getTitleAPI().then(res =>
      setTitles(res.data.map((t: any) => ({
        id: t.Id,
        label: t.PrefixCode
      })))
    );
  }, []);

  // ✅ Load doctor data with image handling
  useEffect(() => {
    if (!userId) {
      return;
    }
    const loadDoctor = async () => {
      try {
        setLoading(true);
        const response = await getDoctorByIdAPI(doctorId, userId);
        const apiData = response.data.tempData;
        
        setOriginalData(response.data.originalData);
        setDoctorData(apiData);
        const transformedData = transformDoctorDataToFormData(apiData);
        setFormData(transformedData);
        
        // ✅ Handle doctor image from tempData
        if (apiData.doctorImage) {
          setTempDataDoctorImage(apiData.doctorImage);
          // Set preview with base URL
          setDoctorImagePreview(`${process.env.REACT_APP_BASE_URL}/${apiData.doctorImage}`);
        }
        
        // Set initial department ID if exists in data
        if (response.data.tempData.departmentId) {
          setSelectedDepartmentId(response.data.tempData.departmentId);
        }
      } catch (error) {
        console.error("Error loading doctor:", error);
        Toast.error("Failed to load doctor data");
      } finally {
        setLoading(false);
      }
    };

    loadDoctor();
  }, [doctorId, userId, transformDoctorDataToFormData]);

  // Form update handler
  const update = useCallback((k: keyof MarketingFormData, v: any) => {
    setFormData(p => p ? { ...p, [k]: v } : p);
    
    if (validationErrors[k as keyof ValidationErrors]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[k as keyof ValidationErrors];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle department selection
  const handleDepartmentChange = useCallback((selectedOption: LookupOption) => {
    setSelectedDepartmentId(selectedOption.id);
    
    // Reset specialization when department changes
    update("specializationId", null);
  }, [update]);

  // Handle specialization selection
  const handleSpecializationChange = useCallback((selectedOption: LookupOption) => {
    update("specializationId", selectedOption.id);
  }, [update]);

  // Role array handlers
  const updateRoleArray = useCallback((
    key: "areaOfInterest" | "previousExperiance",
    index: number,
    field: keyof RoleDescription,
    value: string
  ) => {
    setFormData(prev => {
      if (!prev) return prev;
      const updated = [...prev[key]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [key]: updated };
    });
  }, []);

  const addRoleRow = useCallback((key: "areaOfInterest" | "previousExperiance") => {
    if (!formData) return;
    
    if (hasEmptyFields(formData[key], ["role", "description"])) {
      setDynamicFieldErrors(prev => ({
        ...prev,
        [key]: "Fill in all existing fields before adding a new one."
      }));
      return;
    }
    
    update(key, [...formData[key], { role: "", description: "" }]);
  }, [formData, update]);

  const removeRoleRow = useCallback((
    key: "areaOfInterest" | "previousExperiance",
    index: number
  ) => {
    setFormData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index)
      };
    });
  }, []);

  // ✅ Get Submit Status
  const getSubmitStatus = () => {
    if (!doctorData?.status) return "S";
    
    const normalizedStatus = doctorData.status.toLowerCase().replace(/\s+/g, "");
    
    if (normalizedStatus === "sendback" || normalizedStatus === "sb") {
      return "RC";
    }
    
    return "S";
  };

  const normalizeRoleArray = (value: any): string => {
    if (!value) return "";

    try {
      const arr = typeof value === "string" ? JSON.parse(value) : value;
      return JSON.stringify(
        arr.map((item: any) => ({
          role: (item.role || item.Role || "").trim(),
          description: (item.description || item.Description || "").trim()
        }))
      );
    } catch {
      return "";
    }
  };


  const preparePayload = useCallback((): SaveDraftPayload | null => {
    if (!formData || !userId) return null;

    const isSendBackStatus = () => {
      if (!doctorData?.status) return false;
      const normalized = doctorData.status.toLowerCase().replace(/\s+/g, "");
      return normalized === "sendback" || normalized === "sb";
    };

    let finalDoctorImage = "";
    if (tempDataDoctorImage && !uploadedImagePath) {
      // If tempData has image and no new upload, use tempData image path
      finalDoctorImage = tempDataDoctorImage;
    } else if (uploadedImagePath) {
      // If new image was uploaded, use the uploaded path
      finalDoctorImage = uploadedImagePath;
    }

    return {
      doctorId,
      userId: userId,
      userIp: "string",
      locationId: formData.locationId || 0,
      locationName: formData.locationName,
      doctorName: `${formData.name}`,
      namePrefixCode: formData.namePrefixCode,
      designation: formData.designation,
      specializationId: formData.specializationId || null,
      experienceYears: extractNumericValue(formData.experience),
      doctorFees: extractNumericValue(formData.fee),
      languages: formData.languages,
      qualification: formData.qualification,
      professionalSummary: formData.summary,
      awardsAchievements: formData.awards,
      researchPublications: formData.publications,
      previousExperiance: formData.previousExperiance,
      areaOfInterest: formData.areaOfInterest,
      gender: formData.gender,
      imagePath: finalDoctorImage, 
      phoneNo: formData.phoneNo,
      emailId: formData.emailId,
      registrationNo: formData.registrationNo,
        EmpCode:formData.empCode,
         source:formData.source,
     secondaryLocations: (formData.secondaryLocations || []).map(id => ({
    locationId: id,
  
  })),
      feedback: "",
      status: getSubmitStatus(),
      Remark: buildRemark("SUBMIT"),
      Event: isSendBackStatus()
        ? "Resubmit Profile After Send Back"
        : "Submit Profile"
    };
  }, [formData, doctorData, doctorId, userId, uploadedImagePath, tempDataDoctorImage]);

  const getChangedSections = useCallback((): string[] => {
    if (!originalData || !formData) return [];

    const changes: string[] = [];
    
    if (originalData.locationId !== formData.locationId) {
      changes.push("Location");
    }
    
    if (originalData.specializationId !== formData.specializationId) {
      changes.push("Specialization");
    }
    
    if (normalize(originalData.designation) !== normalize(formData.designation)) {
      changes.push("Designation");
    }

    if (originalData.experienceYears !== extractNumericValue(formData.experience)) {
      changes.push("Experience");
    }

    if (originalData.doctorFees !== extractNumericValue(formData.fee)) {
      changes.push("Consultation Fee");
    }

    if (normalize(originalData.languages) !== normalize(formData.languages)) {
      changes.push("Languages");
    }

    if (normalize(originalData.profileBrief) !== normalize(formData.summary)) {
      changes.push("Profile Brief");
    }

    if (normalizeRoleArray(originalData.areaOfInterest) !== normalizeRoleArray(formData.areaOfInterest)) {
      changes.push("Area of Interest");
    }

    if (normalizeRoleArray(originalData.previousExperiance) !== normalizeRoleArray(formData.previousExperiance)) {
      changes.push("Previous Experience");
    }

    if (normalize(originalData.awardsAchievements) !== normalize(JSON.stringify(formData.awards))) {
      changes.push("Awards & Achievements");
    }

    if (normalize(originalData.researchPublications) !== normalize(JSON.stringify(formData.publications))) {
      changes.push("Research & Publications");
    }

    return changes;
  }, [originalData, formData]);

  const buildRemark = (action: "DRAFT" | "SUBMIT") => {
    const changedSections = getChangedSections();

    if (changedSections.length === 0) {
      return action === "DRAFT"
        ? "Drafted (No Changes)"
        : "Submitted for Approval (No Changes)";
    }

    const changeText = changedSections.join(", ");

    return action === "DRAFT"
      ? `Drafted – Changes in ${changeText}`
      : `Submitted for Approval – Changes in ${changeText}`;
  };

  // Validate mandatory fields
  const validateMandatoryFields = useCallback((): boolean => {
    if (!formData) return false;

    const errors: ValidationErrors = {};
    let isValid = true;
    
    if (!formData.designation?.trim()) {
      errors.designation = "Designation is required";
      isValid = false;
    }
     if (!selectedDepartmentId) {
  errors.departmentName = "Department is required";
  isValid = false;
}

if (!formData.specializationId) {
  errors.specialization = "Specialization is required";
  isValid = false;
}
// Employee Code validation
if (!formData.empCode?.trim()) {
  errors.empCode = "Employee Code is required";
  isValid = false;
}
else if (!/^[A-Z0-9\-\/]+$/.test(formData.empCode)) {
  errors.empCode = "Only A-Z, 0-9, - and / are allowed";
  isValid = false;
}
else if (formData.empCode.length > 30) {
  errors.empCode = "Employee Code cannot exceed 30 characters";
  isValid = false;
}
    if (!formData.namePrefixCode?.trim()) {
      errors.namePrefixCode = "Title is required";
      isValid = false;
    }
    
    if (!formData.experience?.trim()) {
      errors.experience = "Experience is required";
      isValid = false;
    }
    
    if (!formData.fee?.trim()) {
      errors.fee = "Consultation fee is required";
      isValid = false;
    }

    if (!formData.languages?.trim()) {
      errors.languages = "At least one language is required";
      isValid = false;
    }

    if (!formData.summary?.trim()) {
      errors.summary = "Profile Brief is required";
      isValid = false;
    }

    if (!formData.gender?.trim()) {
      errors.gender = "Gender is required";
      isValid = false;
    }
    
    if (!formData.phoneNo?.trim()) {
      errors.phoneNo = "Phone number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.phoneNo.trim())) {
      errors.phoneNo = "Phone number must be 10 digits";
      isValid = false;
    }
    
    if (!formData.emailId?.trim()) {
      errors.emailId = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId.trim())) {
      errors.emailId = "Invalid email format";
      isValid = false;
    }
    
   

    setValidationErrors(errors);

    const dynamicValidation = validateAllDynamicFields();
    
    if (!isValid || !dynamicValidation.isValid) {
      setShowValidationAlert(true);
      
      const issues: string[] = [];
      if (!isValid) issues.push("Fill all mandatory fields");
      if (!dynamicValidation.isValid) issues.push(...dynamicValidation.errors);
      
      setAlert(`⚠️ Please fix the following: ${issues.join(", ")}`);
      setTimeout(() => {
        setShowValidationAlert(false);
        setAlert("");
      }, 5000);
      
      return false;
    }

    return true;
  }, [formData, validateAllDynamicFields]);

  // Save draft
  const saveDraft = useCallback(async () => {
    if (!validateMandatoryFields()) return;
    if (!formData || !doctorData) {
      setAlert("❌ No data to submit");
      setTimeout(() => setAlert(""), 3000);
      return;
    }

    try {
      const payload: SaveDraftPayload = {
        ...preparePayload()!,
        status: "SD",
        Remark: buildRemark("DRAFT"),
        Event: "Profile Draft"
      };

      await saveDraftAndReviewAPI(payload, doctorId);
      Toast.success("Draft saved successfully");
      setTimeout(() => setAlert(""), 3000);
    } catch (error: any) {
      console.error("Error in save draft:", error);
      Toast.error(`${error?.response?.data?.message || "Failed to save. Please try again."}`);
      setTimeout(() => setAlert(""), 5000);
    }
  }, [validateMandatoryFields, formData, doctorData, preparePayload, doctorId]);

  // Reset form
  const resetForm = useCallback(() => {
    if (!originalData) return;

    const resetData = transformDoctorDataToFormData(originalData);
    setFormData(resetData);
    setValidationErrors({});
    setDynamicFieldErrors({});
    
    // Reset department ID for specialization fetch
    setSelectedDepartmentId(null);
    
    // ✅ Reset image states
    if (doctorImagePreview && !tempDataDoctorImage) {
      URL.revokeObjectURL(doctorImagePreview);
    }
    setDoctorImageFile(null);
    setDoctorImagePreview("");
    setUploadedImagePath("");
    
    // ✅ Restore original image from tempData if exists
    if (originalData.doctorImage) {
      setTempDataDoctorImage(originalData.doctorImage);
      setDoctorImagePreview(`${process.env.REACT_APP_BASE_URL}/${originalData.doctorImage}`);
    } else {
      setTempDataDoctorImage("");
    }
    
    Toast.info("Form reset to original data");
    setTimeout(() => setAlert(""), 3000);
  }, [originalData, transformDoctorDataToFormData, doctorImagePreview, tempDataDoctorImage]);

  // Preview handler
  const handlePreview = useCallback(() => {
    if (!validateMandatoryFields()) return;
    setShowPreview(true);
  }, [validateMandatoryFields]);

  // Submit handler
  const submit = useCallback(async () => {
    if (!formData || !doctorData) {
      setAlert("❌ No data to submit");
      setTimeout(() => setAlert(""), 3000);
      return;
    }

    try {
      setAlert("📤 Submitting for review...");
      const payload = preparePayload();
      
      if (!payload) return;

      await saveDraftAndReviewAPI(payload, doctorId);
      Toast.success("Successfully submitted for review");
      setShowPreview(false);
    } catch (error: any) {
      console.error("Error in submit data:", error);
      Toast.warning(`${error?.response?.data?.message || "Failed to submit. Please try again."}`);
      setTimeout(() => setAlert(""), 5000);
    }
  }, [formData, doctorData, preparePayload, doctorId]);

  // Section Header Component
  const SectionHeader: React.FC<{ title: string; section: string; required?: boolean }> = useCallback(({
    title,
    section,
    required = false
  }) => (
    <div className="flex items-center justify-between mt-2 first:mt-0">
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-semibold text-gray-800 uppercase tracking-wide">
          {title}
          {required && <span className="text-red-500 ml-1">*</span>}
        </p>
      </div>
      <button
        onClick={() => setCompareSection(section)}
        className="flex items-center gap-1.5 px-1 py-0.5 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-300 text-orange-700 rounded-lg hover:from-orange-100 hover:to-red-100 text-[10px] shadow-sm hover:shadow transition-all"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Compare
      </button>
    </div>
  ), []);

  // Dynamic Role Field Component
  const DynamicRoleField: React.FC<{
    item: RoleDescription;
    index: number;
    section: "areaOfInterest" | "previousExperiance";
    borderColor: string;
    roleLabel: string;
  }> =
   useCallback(({ item, index, section, borderColor, roleLabel }) => {
    const isRoleEmpty = !item.role.trim();
    const isDescEmpty = !item.description.trim();
    const hasError = dynamicFieldErrors[section];

    return (
      <div className={`bg-white border-2 ${borderColor} p-4 rounded-lg shadow-sm relative hover:shadow-md transition-shadow`}>
        <button
          onClick={() => removeRoleRow(section, index)}
          className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center font-bold shadow-md transition-all hover:scale-110"
        >
          ×
        </button>
        
        <div className="flex items-center relative mb-3 gap-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">{roleLabel}</label>
          <input
            type="text"
            value={item.role}
            onChange={(e) => updateRoleArray(section, index, "role", e.target.value)}
            className={`w-full px-4 py-1 text-sm border-2 rounded-lg transition-all focus:outline-none focus:ring-1 ${
              isRoleEmpty && hasError
                ? "border-red-400 bg-red-50 focus:ring-red-400"
                : "border-gray-300 hover:border-gray-400 focus:ring-blue-500"
            }`}
            placeholder={`Enter ${roleLabel.toLowerCase()}`}
          />
          {isRoleEmpty && hasError && (
            <div className="absolute -bottom-3.5 right-0 text-[10px] text-red-600 font-medium flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Required field
            </div>
          )}
        </div>

        <div className="flex items-start relative gap-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Description :</label>
          <textarea
            value={item.description}
            onChange={(e) => updateRoleArray(section, index, "description", e.target.value)}
            className={`w-full px-4 py-1 text-sm border-2 rounded-lg transition-all focus:outline-none focus:ring-1 ${
              isDescEmpty && hasError
                ? "border-red-400 bg-red-50 focus:ring-red-400"
                : "border-gray-300 hover:border-gray-400 focus:ring-blue-500"
            }`}
            rows={3}
            placeholder="Enter description"
          />
          {isDescEmpty && hasError && (
            <div className="absolute -bottom-3.5 right-0 text-[10px] text-red-600 font-medium flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Required field
            </div>
          )}
        </div>
      </div>
    );
  }, [dynamicFieldErrors, removeRoleRow, updateRoleArray]);

  /* ================= UI ================= */

  if (loading || !formData) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-lg border-2 border-dashed border-blue-300 text-center">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-b-indigo-600 rounded-full animate-spin animation-delay-150"></div>
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-700">Loading doctor profile</p>
            <p className="text-sm text-gray-500 mt-1">Preparing editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg border border-blue-200 h-fit sticky top-6 overflow-hidden">
        <div className="bg-gradient-to-r from-[#184A84] to-[#2563EB] px-6 py-2 border-b-4 border-[#0F3A6F]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edited Profile
          </h2>
        </div>

        {alert && (
          <div className={`mx-6 mt-4 p-3 border-l-4 rounded-lg shadow-sm flex items-center gap-2 ${
            showValidationAlert 
              ? "bg-gradient-to-r from-red-50 to-orange-50 border-red-500 text-red-800"
              : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-500 text-green-800"
          }`}>
            <span className="font-medium text-xs">{alert}</span>
          </div>
        )}

        <div className="space-y-1 h-[calc(100vh-240px)] overflow-y-auto p-6 custom-scrollbar">
          {/* ✅ Doctor Image Section */}
          <div className="border-t-2 border-blue-200 pt-4 mt-4">
            
            <p className="text-[10px] font-semibold text-gray-800 uppercase tracking-wide">
        Doctor Image
         <span className="text-red-500 ml-1">*</span>
        </p>  
            
            <div className="flex gap-4 items-start">
              {/* Image Preview */}
              <div className="w-35 h-40 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                {doctorImagePreview ? (
                  <img 
                    src={doctorImagePreview} 
                    alt="Doctor" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img 
                    src={Doctor} 
                    alt="Doctor" 
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Upload/Delete Controls */}
              <div className="flex flex-col gap-2 flex-1">
                <div className="text-xs text-gray-600 mb-2">
                  <p>• Only WEBP format accepted</p>
                  <p>• Maximum file size: 5MB</p>
                  {tempDataDoctorImage && (
                    <p className="text-green-600 font-medium">✓ Using existing doctor image</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <label className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer text-sm font-medium flex items-center gap-2 transition-all">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {tempDataDoctorImage ? "Change Image" : "Upload Image"}
                    <input
                      type="file"
                      accept=".webp"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>

                  {doctorImagePreview && (
                    <button
                      onClick={handleDeletePhoto}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium flex items-center gap-2 transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {validationErrors.doctorImage && (
              <ValidationErrorMessage message={validationErrors.doctorImage} />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <SectionHeader title="Title" section="namePrefixCode" required />
              <SearchableDropdown
                value={formData.namePrefixCode}
                placeholder="Select title"
                options={titles}
                onSelect={v => update("namePrefixCode", v.label)}
              />
              {validationErrors.namePrefixCode && (
                <ValidationErrorMessage message={validationErrors.namePrefixCode} />
              )}
            </div>
            <div>
              <SectionHeader title="Doctor Name" section="name" required />
              <FormField
                label=""
                type="text"
                value={formData.name}
                onChange={(v) => update("name", v)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
              <SectionHeader title="Location" section="location" required />
             
<SearchableDropdown
  value={formData.locationName}
  placeholder="Primary location"
  options={locations}
  onSelect={(v) => {
    update("locationName", v.label);
    update("locationId", v.id);
    
    if (formData.secondaryLocations?.includes(v.id)) {
      update("secondaryLocations", formData.secondaryLocations.filter(id => id !== v.id));
    }
  }}
/>

            </div>
{/* Secondary Locations */}
<div className="mb-4">
  <SectionHeader
    title="Secondary Locations"
    section="secondaryLocations"
  />
  <div className="">
    {availableSecondaryLocationOptions.length === 0 ? (
      <p className="text-xs text-gray-500">
        Select a primary location first, then choose secondary locations.
      </p>
    ) : (
      <LocationMultiSelect
        locations={availableSecondaryLocationOptions}
        selectedLocationIds={
          formData.secondaryLocations
            ? formData.secondaryLocations.join(",")
            : ""
        }
        onChange={(selectedIds: string) => {
          const ids = selectedIds
            ? selectedIds
                .split(",")
                .map((id) => parseInt(id.trim()))
                .filter((id) => !isNaN(id))
            : [];
          update("secondaryLocations", ids);
        }}
      />
    )}
  </div>
</div>



            

          </div>
 <div className="grid grid-cols-2 gap-4 mb-4"><div> <SectionHeader title="Languages" section="languages" required />
          <LanguageMultiSelect
            languages={availableLanguages}
            selectedLanguages={formData.languages}
            onChange={(v) => update("languages", v)}
            disabled={false}
          /></div>
            <div>
              <SectionHeader title="Designation" section="designation" required />
              <SearchableDropdown
                value={formData.designation}
                placeholder="Select designation"
                options={designations}
                onSelect={v => {
                  update("designation", v.label);
                  update("designationId", v.id);
                }}
              />
              {validationErrors.designation && (
                <ValidationErrorMessage message={validationErrors.designation} />
              )}
            </div></div>
          {/* Department and Specialization */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <SectionHeader title="Department" section="department"  required />
              <SearchableDropdown
                value={
                  selectedDepartmentId
                    ? departments.find(d => d.DepartmentId === selectedDepartmentId)?.DepartmentName || ""
                    : ""
                }
                placeholder="Select department"
                options={departments.map(dept => ({
                  id: dept.DepartmentId,
                  label: dept.DepartmentName
                }))}
                onSelect={handleDepartmentChange}
              />
            </div>

            <div>
              <SectionHeader title="Specialization" section="specialization"  required />
              <SearchableDropdown
                value={
                  formData.specializationId
                    ? specializations.find(s => s.SpecializationId === formData.specializationId)?.SpecializationName || ""
                    : ""
                }
                placeholder={selectedDepartmentId ? "Select specialization" : "Select department first"}
                options={specializations.map(spec => ({
                  id: spec.SpecializationId,
                  label: spec.SpecializationName
                }))}
                onSelect={handleSpecializationChange}
              />
              {validationErrors.specialization && (
                <ValidationErrorMessage message={validationErrors.specialization} />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <SectionHeader title="Gender" section="gender" required />
              <select
                value={formData.gender}
                onChange={(e) => update("gender", e.target.value)}
                className="w-full px-4 py-1 text-xs border border-1 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
              {validationErrors.gender && <ValidationErrorMessage message={validationErrors.gender} />}
            </div>
            <div>
              <SectionHeader title="Phone Number" section="phoneNo" required />
              <FormField
                label=""
                type="text"
                value={formData.phoneNo}
                onChange={(v) => {
                  const numericValue = v.replace(/\D/g, '').slice(0, 10);
                  update("phoneNo", numericValue);
                }}
                placeholder="Enter phone number"
              />
              {validationErrors.phoneNo && <ValidationErrorMessage message={validationErrors.phoneNo} />}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <SectionHeader title="Email ID" section="emailId" required />
              <FormField
                label=""
                type="text"
                value={formData.emailId}
                onChange={(v) => update("emailId", v)}
                placeholder="Enter email address"
              />
              {validationErrors.emailId && <ValidationErrorMessage message={validationErrors.emailId} />}
            </div>
            
            <div>
              <SectionHeader title="Registration No" section="registrationNo"  />
              <FormField
                label=""
                type="text"
                value={formData.registrationNo}
                onChange={(v) => update("registrationNo", v.toUpperCase())}
                placeholder="Enter registration number"
                
              />
            
            </div>
          </div>
            <div>
    <SectionHeader title="Employee Code" section="empCode" required/>

<div className="relative">
  <input
    type="text"
    value={formData.empCode}
    onChange={(e) =>
  doctorData?.source === "MKT"
    ? update("empCode", handleEmployeeCodeChange(e.target.value))
    : undefined
}
    placeholder={
      doctorData?.source === "MKT"
        ? "Enter employee code"
        : "Auto-filled from source"
    }
    maxLength={30}
    disabled={doctorData?.source !== "MKT"}
    className={`uppercase w-full px-4 py-1 text-xs border rounded-lg focus:outline-none focus:ring-2 ${
      doctorData?.source === "MKT"
        ? "border-gray-300 focus:ring-blue-500 bg-white"
        : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
    }`}
  />

  {doctorData?.source !== "MKT" && (
    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 bg-gray-100 px-1 rounded">
      locked
    </span>
  )}
</div>

{validationErrors.empCode && (
  <ValidationErrorMessage message={validationErrors.empCode} />
)}

{doctorData?.source !== "MKT" && (
  <p className="text-[10px] text-gray-400 mt-0.5">
    Editable only for MKT source
  </p>
)}
  </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <SectionHeader title="Experience" section="experience" required />
              <FormField
                label=""
                type="number"
                value={formData.experience}
                onChange={(v) => update("experience", v)}
              />
              {validationErrors.experience && <ValidationErrorMessage message={validationErrors.experience} />}
            </div>
            <div>
              <SectionHeader title="Consultation Fee" section="fee" required />
              <FormField
                label=""
                type="text"
                value={formData.fee}
                onChange={(v) => update("fee", v)}
              />
              {validationErrors.fee && <ValidationErrorMessage message={validationErrors.fee} />}
            </div>
          </div>

         
          {validationErrors.languages && <ValidationErrorMessage message={validationErrors.languages} />}

          <SectionHeader title="Qualification" section="qualification" />
          <FormField
            label=""
            type="textarea"
            value={formData.qualification}
            onChange={(v) => update("qualification", v)}
          />

          <SectionHeader title="Profile Brief" section="summary" required />
          <FormField
            label=""
            type="textarea"
            value={formData.summary}
            onChange={(v) => update("summary", v)}
            placeholder="Add a marketing-friendly profile summary..."
          />
          {validationErrors.summary && <ValidationErrorMessage message={validationErrors.summary} />}

          {/* Area of Interest */}
          <SectionHeader title="Area of Interest" section="areaOfInterest" />
          <div className="space-y-1">
            {formData.areaOfInterest.map((item, idx) => (
              <DynamicRoleField
                key={idx}
                item={item}
                index={idx}
                section="areaOfInterest"
                borderColor="border-blue-200"
                roleLabel="Role/Specialty"
              />
            ))}
          </div>

          {dynamicFieldErrors.areaOfInterest && <ErrorBanner message={dynamicFieldErrors.areaOfInterest} />}

          <button
            className="flex items-center gap-2 px-4 py-1 to-emerald-50 border-1 border border-green-400 text-green-700 rounded-lg hover:from-green-100 hover:to-emerald-100 font-semibold text-xs shadow-sm hover:shadow transition-all w-fit justify-center group"
            onClick={() => addRoleRow("areaOfInterest")}
          >
            <svg className="w-4 h-4 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Area of Interest
          </button>

          {/* Previous Experience */}
          <SectionHeader title="Previous Experience" section="previousExperiance" />
          <div className="space-y-3">
            {formData.previousExperiance.map((item, idx) => (
              <DynamicRoleField
                key={idx}
                item={item}
                index={idx}
                section="previousExperiance"
                borderColor="border-green-200"
                roleLabel="Role / Hospital"
              />
            ))}
          </div>

          {dynamicFieldErrors.previousExperiance && <ErrorBanner message={dynamicFieldErrors.previousExperiance} />}

          <button
            className="flex items-center gap-2 px-4 py-1 to-emerald-50 border-1 border border-green-400 text-green-700 rounded-lg hover:from-green-100 hover:to-emerald-100 font-semibold text-xs shadow-sm hover:shadow transition-all w-fit justify-center group"
            onClick={() => addRoleRow("previousExperiance")}
          >
            <svg className="w-4 h-4 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Experience
          </button>

          {/* Awards & Achievements */}
          <div className="border border-1 border-gray-300 p-2 bg-white rounded-lg">
            <SectionHeader title="Awards & Achievements" section="awards" />
            <DynamicArray
              label="Awards & Achievements"
              items={formData.awards}
              onChange={(v) => update("awards", v)}
            />
          </div>

          {/* Research & Publications */}
          <div className="border border-1 border-gray-300 p-2 bg-white rounded-lg">
            <SectionHeader title="Research & Publications" section="publications" />
            <DynamicArray
              label="Research & Publications"
              items={formData.publications}
              onChange={(v) => update("publications", v)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-3 border-t-2 border-blue-200 flex gap-3 shadow-lg">
          <Button variant="primary" onClick={saveDraft}>
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save Draft
          </Button>
          <Button variant="success" onClick={handlePreview}>
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </Button>
          <Button variant="danger" onClick={resetForm}>
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset
          </Button>
        </div>
      </div>

      <PreviewModal
        isOpen={showPreview}
        data={formData}
        payload={preparePayload()}
        doctorId={doctorId}
        doctorImage={doctorImagePreview}
        onClose={() => setShowPreview(false)}
        onSubmit={submit}
        onModalClose={onClose}
      />

      {compareSection && originalData && formData && (
        <CompareModal
          section={compareSection}
          originalData={originalData}
          marketingData={formData}
          onClose={() => setCompareSection(null)}
        />
      )}
    </>
  );
};

export default MarketingEditor;
